import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import styles from "./ScheduleDemo.module.css";

// Material UI components
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";

const ScheduleDemo = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    jobTitle: "",
    companySize: "",
    serviceInterest: "",
    preferredDate: "",
    preferredTime: "",
    message: "",
    agreeToTerms: false,
  });

  const recaptchaRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [status, setStatus] = useState(null);

  const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const apiURL = process.env.REACT_APP_API_URL_SCHEDULE;

  const handleCaptchaChange = (token) => setCaptchaToken(token);
  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      setCaptchaToken(null);
    }
  };

  const onCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const companySizeOptions = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "501-1000 employees",
    "1000+ employees",
  ];

  const serviceOptions = [
    "Provider Credentialing",
    "Primary Source Verification",
    "Continuous Monitoring",
    "Compliance Solutions",
    "Full Service Management",
    "Other",
  ];

  const timeOptions = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM",
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^\S+@\S+\.\S+$/;
    const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Phone number is invalid";
    }

    if (!formData.companyName.trim())
      newErrors.companyName = "Company name is required";
    if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job title is required";
    if (!formData.companySize)
      newErrors.companySize = "Company size is required";
    if (!formData.serviceInterest)
      newErrors.serviceInterest = "Please select a service interest";
    if (!formData.preferredDate)
      newErrors.preferredDate = "Please select a preferred date";
    if (!formData.preferredTime)
      newErrors.preferredTime = "Please select a preferred time";

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the terms";
    }

    if (!captchaToken) {
      newErrors.captcha = "Please verify you are not a robot";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    //console.log("Submitting captcha token:", captchaToken);

    const payload = { ...formData, captcha: captchaToken };

    try {
      const res = await fetch(apiURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setStatus({ ok: true, message: "Meet scheduled successfully!" });
        setSubmitted(true);
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          companyName: "",
          jobTitle: "",
          companySize: "",
          preferredDate: "",
          preferredTime: "",
          serviceInterest: "",
          message: "",
        });
        resetCaptcha();
      } else {
        setStatus({
          ok: false,
          message: data.message || "Submission failed.",
        });
        resetCaptcha();
      }
    } catch (err) {
      console.error("Network error:", err);
      setStatus({ ok: false, message: "Network error — try again." });
      resetCaptcha();
    }
  };

  return (
    <Container maxWidth="md" className={styles["schedule-demo-container"]}>
      <title>Schedule a Demo | iCredentialing Provider Credentialing Platform</title>
      <meta name="description" content="Schedule a personalized demo to see how iCredentialing streamlines provider credentialing, onboarding, and healthcare compliance through automation."/>
      <meta
          name="keywords"
          content="schedule credentialing demo, healthcare credentialing software demo, provider onboarding platform, iCredentialing demo"
      />
      <link
          rel="canonical"
          href={`https://icredentialing.com/schedule-demo`}
      />
      <Typography
        variant="h4"
        component="h1"
        className={styles["demo-title"]}
        gutterBottom
      >
        Schedule a Personalized Meet
      </Typography>

      <Typography
        variant="subtitle1"
        paragraph
        className={styles["demo-subtitle"]}
      >
        See how our credentialing solutions can benefit your organization
      </Typography>

      {submitted && (
        <Alert severity="success" sx={{ mb: 4 }}>
          Thank you for scheduling a meeting! Our team will contact you shortly
          to confirm your appointment.
        </Alert>
      )}

      <Paper elevation={3} className={styles["demo-paper"]}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Box className={styles["demo-info"]}>
              <Typography
                variant="h6"
                gutterBottom
                className={styles["demo-subheader"]}
              >
                What to Expect During Your Introductory Meeting
              </Typography>

              <Box component="ul" className={styles["demo-list"]}>
                <li>
                  <Typography
                    component="span"
                    className={styles["demo-list-number"]}
                  >
                    1
                  </Typography>
                  <Typography
                    component="span"
                    className={styles["demo-list-content"]}
                  >
                    <strong>Personalized Overview</strong> - Discover how
                    iCredentialing is transforming provider credentialing and
                    compliance through automation.
                  </Typography>
                </li>
                <li>
                  <Typography
                    component="span"
                    className={styles["demo-list-number"]}
                  >
                    2
                  </Typography>
                  <Typography
                    component="span"
                    className={styles["demo-list-content"]}
                  >
                    <strong>Needs Assessment</strong> - Share your current
                    challenges so we can align our solutions to your goals.
                  </Typography>
                </li>
                <li>
                  <Typography
                    component="span"
                    className={styles["demo-list-number"]}
                  >
                    3
                  </Typography>
                  <Typography
                    component="span"
                    className={styles["demo-list-content"]}
                  >
                    <strong>Feature Preview</strong> - Get a first look at how
                    our platform streamlines credentialing, monitoring, and data
                    exchange.
                  </Typography>
                </li>
                <li>
                  <Typography
                    component="span"
                    className={styles["demo-list-number"]}
                  >
                    4
                  </Typography>
                  <Typography
                    component="span"
                    className={styles["demo-list-content"]}
                  >
                    <strong>Collaboration Opportunities</strong> - Learn how to
                    join as an early adopter or pilot participant.
                  </Typography>
                </li>
                <li>
                  <Typography
                    component="span"
                    className={styles["demo-list-number"]}
                  >
                    5
                  </Typography>
                  <Typography
                    component="span"
                    className={styles["demo-list-content"]}
                  >
                    <strong>Exclusive Pilot Program</strong> - The first 30
                    organizations receive a free trial to test iCredentialing’s
                    efficiency and performance.
                  </Typography>
                </li>
              </Box>

              {/* <Typography variant="subtitle2" sx={{ mt: 4, mb: 2 }} className={styles["demo-content"]}>
                Meet Duration: Approximately 30-45 minutes
              </Typography>
               */}
              <Typography variant="body2" className={styles["demo-content"]}>
                Have questions before scheduling?
                <br />
                Contact us at <strong>(949) 390-2661</strong> or
                <br />
                <a
                  href="mailto:info@iCredentialing.com"
                  className={styles["demo-contact-link"]}
                >
                  info@iCredentialing.com
                </a>
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              className={styles["demo-form"]}
            >
              <Typography
                variant="h6"
                className={styles["contact-title"]}
                gutterBottom
              >
                Contact Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    error={!!errors.jobTitle}
                    helperText={errors.jobTitle}
                    required
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    error={!!errors.companySize}
                    className={styles.selectSmall}
                  >
                    <InputLabel id="company-size-label">
                      Company Size
                    </InputLabel>
                    <Select
                      labelId="company-size-label"
                      name="companySize"
                      value={formData.companySize}
                      onChange={handleChange}
                      label="Company Size"
                      required
                    >
                      {companySizeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.companySize && (
                      <Typography color="error" variant="caption">
                        {errors.companySize}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    error={!!errors.serviceInterest}
                    className={styles.selectSmall}
                  >
                    <InputLabel id="service-interest-label">
                      Service Interest
                    </InputLabel>
                    <Select
                      labelId="service-interest-label"
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleChange}
                      label="Service Interest"
                      required
                    >
                      {serviceOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.serviceInterest && (
                      <Typography color="error" variant="caption">
                        {errors.serviceInterest}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ mt: 2 }}
                    className={styles["demo-content2"]}
                  >
                    Schedule Information
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Preferred Date"
                    name="preferredDate"
                    type="date"
                    value={formData.preferredDate}
                    onChange={handleChange}
                    error={!!errors.preferredDate}
                    helperText={errors.preferredDate}
                    InputLabelProps={{ shrink: true }}
                    className={styles.selectDate}
                    required
                    inputProps={{
                      min: new Date().toISOString().split("T")[0], // Today's date
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    error={!!errors.preferredTime}
                    className={styles.selectSmall}
                  >
                    <InputLabel
                      id="preferred-time-label"
                      className={styles["demo-content"]}
                    >
                      Preferred Time
                    </InputLabel>
                    <Select
                      labelId="preferred-time-label"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      label="Preferred Time"
                      className={styles["demo-content"]}
                      required
                    >
                      {timeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.preferredTime && (
                      <Typography
                        color="error"
                        variant="caption"
                        className={styles["demo-content"]}
                      >
                        {errors.preferredTime}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Additional Information or Questions"
                    name="message"
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className={styles["contact-input"]}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label="I agree to receive communications about iCredentialing Solutions services"
                    className={styles.selectSmall}
                  />
                  {errors.agreeToTerms && (
                    <Typography
                      color="error"
                      variant="caption"
                      display="block"
                      className={styles["demo-content"]}
                    >
                      {errors.agreeToTerms}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={SITE_KEY_V2}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                  />
                  {errors.captcha && (
                    <div className={styles.errorText}>{errors.captcha}</div>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      mt: 2,
                      borderRadius: "90px",
                      padding: "12px 28px",
                      backgroundColor: "#2e7d98",
                      "&:hover": { backgroundColor: "#1c6a85" },
                      fontSize: "1rem",
                      fontWeight: 600,
                    }}
                    fullWidth
                    className={styles.buttonstyle}
                  >
                    Schedule Meet
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ScheduleDemo;
